import { useRef, useCallback, useEffect } from 'react';
import { AntAuth } from 'ante-react-kit';
import hash from 'hash-sum';

const MAX_CACHE = 800;
const CACHE_NAME = 'BusinessEntity-CustomerPickerRecentResults';
const CACHE_SCHEMA = 'schema:1';

function normalize(str) {
    if (!str) return '';
    return String(str)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .trim();
}

function getCacheKey() {
    const businessCode = AntAuth.getSessionInfo('business_code') || '';
    const userCode = AntAuth.getSessionInfo('user_code') || '';
    const sessionHash = hash(AntAuth.getSessionToken() || '');
    return ['CacheData', CACHE_NAME, CACHE_SCHEMA, businessCode, userCode, sessionHash].join('|');
}

function loadFromStorage() {
    try {
        const raw = localStorage.getItem(getCacheKey());
        if (!raw) return { map: new Map(), order: [] };
        const data = JSON.parse(raw);
        const map = new Map();
        (data.rows || []).forEach((r) => { if (r && r.id != null) map.set(r.id, r); });
        return { map, order: (data.order || []).filter((id) => map.has(id)) };
    } catch (e) {
        return { map: new Map(), order: [] };
    }
}

function saveToStorage(map, order) {
    try {
        const trimOrder = order.slice(-MAX_CACHE);
        const rows = trimOrder.map((id) => map.get(id)).filter(Boolean);
        localStorage.setItem(getCacheKey(), JSON.stringify({ rows, order: trimOrder, savedAt: Date.now() }));
    } catch (e) { /* quota exceeded — ignore */ }
}

function rowMatches(row, normQuery) {
    if (!normQuery) return true;
    const tokens = normQuery.split(/\s+/).filter(Boolean);
    const haystack = normalize([
        row.entity_name, row.entity_full_name, row.entity_contact_phone,
        row.code, row.entity_code, row.entity_legal_id, row.entity_adm1_name
    ].filter(Boolean).join(' '));
    return tokens.every((t) => haystack.includes(t));
}

export default function useCustomerCache() {
    const cacheRef = useRef(null);
    const dirtyRef = useRef(false);

    if (!cacheRef.current) cacheRef.current = loadFromStorage();

    const searchLocal = useCallback((query, extraConditions, limit = 50) => {
        const normQuery = normalize(query);
        const { map, order } = cacheRef.current;
        const out = [];
        const extraKeys = extraConditions ? Object.keys(extraConditions).filter((k) => {
            const v = extraConditions[k];
            return v && (Array.isArray(v) ? v.length > 0 : true);
        }) : [];
        for (let i = order.length - 1; i >= 0 && out.length < limit; i--) {
            const row = map.get(order[i]);
            if (!row) continue;
            if (!rowMatches(row, normQuery)) continue;
            let passExtra = true;
            for (const k of extraKeys) {
                const filterVals = Array.isArray(extraConditions[k]) ? extraConditions[k] : [extraConditions[k]];
                const rowVal = row[k];
                if (Array.isArray(rowVal)) {
                    if (!filterVals.some((fv) => rowVal.includes(fv))) { passExtra = false; break; }
                } else {
                    if (!filterVals.includes(rowVal)) { passExtra = false; break; }
                }
            }
            if (passExtra) out.push(row);
        }
        return out;
    }, []);

    const mergeFromServer = useCallback((rows) => {
        if (!rows || rows.length === 0) return;
        const { map, order } = cacheRef.current;
        const seen = new Set(order);
        rows.forEach((r) => {
            if (!r || r.id == null) return;
            map.set(r.id, r);
            if (!seen.has(r.id)) { order.push(r.id); seen.add(r.id); }
            else {
                const idx = order.indexOf(r.id);
                if (idx >= 0) { order.splice(idx, 1); order.push(r.id); }
            }
        });
        if (order.length > MAX_CACHE) {
            const removed = order.splice(0, order.length - MAX_CACHE);
            removed.forEach((id) => map.delete(id));
        }
        dirtyRef.current = true;
    }, []);

    useEffect(() => {
        const flush = () => {
            if (!dirtyRef.current) return;
            dirtyRef.current = false;
            const { map, order } = cacheRef.current;
            saveToStorage(map, order);
        };
        const t = setInterval(flush, 3000);
        window.addEventListener('beforeunload', flush);
        return () => { clearInterval(t); flush(); window.removeEventListener('beforeunload', flush); };
    }, []);

    return { searchLocal, mergeFromServer };
}
